import React, { useMemo } from 'react';
import {
  TableRow,
  TableCell,
  TextField,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  FormControl,
  Box,
} from '@mui/material';
import { AdsTableFilters, AdStatus, Ad } from '../../../types/ads.types';
import DatePicker from './DatePicker';
import { MinMaxFilterCell } from './table-cells/MinMaxFilterCell';

interface FilterRowProps {
  filters: AdsTableFilters;
  onFiltersChange: (filters: AdsTableFilters) => void;
  showGraphs: boolean;
  ads: Ad[];
}

const ALL_STATUSES: AdStatus[] = ['ACTIVE', 'PAUSED', 'ARCHIVED', 'DELETED', 'UNKNOWN'];

/**
 * FilterRow - Renders filter inputs below table headers
 * Aligns with each column for easy filtering
 */
export const FilterRow: React.FC<FilterRowProps> = ({ filters, onFiltersChange, showGraphs, ads }) => {
  // Get unique advertisers from ads
  const uniqueAdvertisers = useMemo(() => {
    const advertisers = new Set<string>();
    ads.forEach((ad) => {
      if (ad.advertiserName) {
        advertisers.add(ad.advertiserName);
      }
    });
    return Array.from(advertisers).sort();
  }, [ads]);

  const handleFilterChange = (key: keyof AdsTableFilters, value: unknown) => {
    onFiltersChange({
      ...filters,
      [key]: value,
    });
  };

  const inputSx = {
    '& .MuiInputBase-root': {
      fontSize: '0.75rem',
      bgcolor: 'white',
    },
    '& .MuiInputBase-input': {
      py: 0.5,
      px: 1,
    },
  };

  return (
    <TableRow sx={{
      position: 'sticky',
      top: 53,
      zIndex: 99,
      bgcolor: '#fafafa',
      borderTop: 1,
      borderColor: 'divider'
    }}>
      {/* Checkbox column - empty */}
      <TableCell padding="checkbox" sx={{ width: 35, bgcolor: '#fafafa' }} />

      {/* Status filter - Multi-select */}
      <TableCell sx={{ width: 70, p: 0.5, bgcolor: '#fafafa' }}>
        <FormControl size="small" sx={{ width: '100%' }}>
          <Select
            multiple
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            renderValue={(selected) => `${(selected as AdStatus[]).length} SELECTED`}
            sx={{
              fontSize: '0.75rem',
              bgcolor: 'white',
              width: '100%',
              '& .MuiSelect-select': {
                py: 0.5,
                px: 1,
              },
            }}
          >
            {ALL_STATUSES.map((status) => (
              <MenuItem key={status} value={status}>
                <Checkbox
                  checked={filters.status.includes(status)}
                  size="small"
                  sx={{ py: 0, px: 0.5 }}
                />
                <ListItemText
                  primary={status}
                  primaryTypographyProps={{ fontSize: '0.75rem' }}
                />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </TableCell>

      {/* Budget column - empty (no filter) */}
      <TableCell sx={{ width: 100, p: 0.5, bgcolor: '#fafafa' }} />

      {/* Campaign/Ad Name - Text search */}
      <TableCell sx={{ width: 350, p: 0.5, bgcolor: '#fafafa' }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search campaign/ad..."
          value={filters.campaign}
          onChange={(e) => handleFilterChange('campaign', e.target.value)}
          sx={inputSx}
        />
      </TableCell>

      {/* Advertiser - Select */}
      <TableCell sx={{ width: 110, p: 0.5, bgcolor: '#fafafa' }}>
        <FormControl fullWidth size="small">
          <Select
            value={filters.advertiser}
            onChange={(e) => handleFilterChange('advertiser', e.target.value)}
            displayEmpty
            sx={{
              fontSize: '0.75rem',
              bgcolor: 'white',
              '& .MuiSelect-select': {
                py: 0.5,
                px: 1,
              },
            }}
          >
            <MenuItem value="" sx={{ fontSize: '0.75rem' }}>
              All
            </MenuItem>
            {uniqueAdvertisers.map((advertiser) => (
              <MenuItem key={advertiser} value={advertiser} sx={{ fontSize: '0.75rem' }}>
                {advertiser}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </TableCell>

      {/* Spend - Min/Max */}
      <MinMaxFilterCell
        width={75}
        minKey="minSpend"
        maxKey="maxSpend"
        filters={filters}
        onFiltersChange={onFiltersChange}
      />

      {/* Revenue - Min/Max */}
      <MinMaxFilterCell
        width={75}
        minKey="minRevenue"
        maxKey="maxRevenue"
        filters={filters}
        onFiltersChange={onFiltersChange}
      />

      {/* P&L - Min/Max */}
      <MinMaxFilterCell
        width={75}
        minKey="minPnl"
        maxKey="maxPnl"
        filters={filters}
        onFiltersChange={onFiltersChange}
      />

      {/* ROAS - Min/Max */}
      <MinMaxFilterCell
        width={75}
        minKey="minRoas"
        maxKey="maxRoas"
        filters={filters}
        onFiltersChange={onFiltersChange}
      />

      {/* RPM - Min/Max */}
      <MinMaxFilterCell
        width={65}
        minKey="minRpm"
        maxKey="maxRpm"
        filters={filters}
        onFiltersChange={onFiltersChange}
      />

      {/* RPC - Min/Max */}
      <MinMaxFilterCell
        width={65}
        minKey="minRpc"
        maxKey="maxRpc"
        filters={filters}
        onFiltersChange={onFiltersChange}
      />

      {/* CPA - Min/Max */}
      <MinMaxFilterCell
        width={65}
        minKey="minCpa"
        maxKey="maxCpa"
        filters={filters}
        onFiltersChange={onFiltersChange}
      />

      {/* CPC - Min/Max */}
      <MinMaxFilterCell
        width={65}
        minKey="minCpc"
        maxKey="maxCpc"
        filters={filters}
        onFiltersChange={onFiltersChange}
      />

      {/* Clicks - Min/Max */}
      <MinMaxFilterCell
        width={60}
        minKey="minClicks"
        maxKey="maxClicks"
        filters={filters}
        onFiltersChange={onFiltersChange}
      />

      {/* Searches - Min/Max */}
      <MinMaxFilterCell
        width={60}
        minKey="minSearches"
        maxKey="maxSearches"
        filters={filters}
        onFiltersChange={onFiltersChange}
      />

      {/* Page CTR - Min/Max */}
      <MinMaxFilterCell
        width={70}
        minKey="minPageCtr"
        maxKey="maxPageCtr"
        filters={filters}
        onFiltersChange={onFiltersChange}
      />

      {/* Created - Date filters */}
      <TableCell sx={{ width: 90, p: 0.5, bgcolor: '#fafafa' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: '100%' }}>
          <DatePicker
            value={filters.adCreatedFrom}
            onChange={(value) => handleFilterChange('adCreatedFrom', value)}
            placeholder="From"
          />
          <DatePicker
            value={filters.adCreatedTo}
            onChange={(value) => handleFilterChange('adCreatedTo', value)}
            placeholder="To"
          />
        </Box>
      </TableCell>

      {/* Updated - Date filters */}
      <TableCell sx={{ width: 90, p: 0.5, bgcolor: '#fafafa' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: '100%' }}>
          <DatePicker
            value={filters.updatedFrom}
            onChange={(value) => handleFilterChange('updatedFrom', value)}
            placeholder="From"
          />
          <DatePicker
            value={filters.updatedTo}
            onChange={(value) => handleFilterChange('updatedTo', value)}
            placeholder="To"
          />
        </Box>
      </TableCell>

      {/* Trend Chart - Empty */}
      {showGraphs && <TableCell sx={{ width: 70, p: 0.5 }} />}

      {/* Coverage - Min/Max */}
      <MinMaxFilterCell
        width={70}
        minKey="minCoverage"
        maxKey="maxCoverage"
        filters={filters}
        onFiltersChange={onFiltersChange}
      />
    </TableRow>
  );
};

export default FilterRow;
